import { ApolloError, useSubscription } from "@apollo/client";

import type { ListenToDeviceDataSubscription } from "../../../API";
import { isNetworkHealthOnline } from "../../../common/helpers/isNetworkHealthOnline";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { LISTEN_TO_DEVICE_DATA } from "../../../common/operations/subscriptions";

interface UseListenToDeviceDataChangeParams {
  currentDeviceId?: string;
  onError?: (error: ApolloError) => void;
}

export const useListenToDeviceDataChange = ({
  currentDeviceId,
  onError,
}: UseListenToDeviceDataChangeParams = {}) => {
  const customerId = useCustomerIdGuard();

  const { data, loading, error } =
    useSubscription<ListenToDeviceDataSubscription>(LISTEN_TO_DEVICE_DATA, {
      fetchPolicy: "no-cache",
      variables: {
        customerId,
      },
      shouldResubscribe: true,
      onData: ({ client, data }) => {
        const device = data?.data?.listenToDeviceData;

        if (device && currentDeviceId && device.id === currentDeviceId) {
          const deviceId = `Device:${device.id}`;

          client.cache.writeQuery({
            query: LISTEN_TO_DEVICE_DATA,
            data: data?.data,
            variables: { id: deviceId },
          });

          // const message = `Device ${device.name} is updated with new position`;
          // successNotification(message);
        }
      },
      onError,
    });

  let isOnline = false;

  try {
    if (data?.listenToDeviceData?.healthData) {
      const deviceHealthData = data.listenToDeviceData.healthData;

      isOnline = deviceHealthData?.isOnline
        ? deviceHealthData.isOnline
        : isNetworkHealthOnline(+data.listenToDeviceData.healthData);
    }
  } catch (err) {
    console.error("Failed to parse device health data JSON:", err);
  }

  return {
    data,
    loading,
    error,
    isOnline,
    deviceData: data?.listenToDeviceData?.deviceData,
    currentPosition: data?.listenToDeviceData?.currentPosition,
  };
};
